
import { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush
} from 'recharts';
import { StockData } from '../data/sampleData';

interface BacktestChartProps {
  stockData: StockData;
  title?: string;
}

const BacktestChart = ({ stockData, title }: BacktestChartProps) => {
  const [chartData, setChartData] = useState<any[]>([]);
  
  useEffect(() => {
    if (!stockData || !stockData.data || stockData.data.length === 0) {
      return;
    }
    
    // Generate some sample backtest data
    const data = stockData.data.map((dataPoint, index) => {
      // Calculate errors based on position in the array for more realistic simulation
      // Earlier predictions have higher error rates
      const progress = index / stockData.data.length;
      const errorMultiplier = 1 - (progress * 0.5);
      
      const lstmError = (Math.random() * 0.02 - 0.01) * errorMultiplier;
      const rlError = (Math.random() * 0.035 - 0.02) * errorMultiplier;
      
      return {
        date: dataPoint.date,
        actual: dataPoint.value,
        lstm: dataPoint.value * (1 + lstmError),
        rl: dataPoint.value * (1 + rlError),
      };
    });
    
    setChartData(data);
  }, [stockData]);

  const formatYAxis = (value: number): string => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  };
  
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip bg-card border p-3 rounded-md shadow-sm">
          <p className="label text-sm font-medium">{`Date: ${new Date(label).toLocaleDateString()}`}</p>
          <div className="flex flex-col gap-1 mt-1">
            {payload.map((entry: any, index: number) => (
              <p key={`item-${index}`} style={{ color: entry.color }}>
                <span className="font-medium">{entry.name}: </span>
                {entry.value.toFixed(2)}
              </p>
            ))}
            {payload.length > 1 && payload[0]?.value && payload[1]?.value && (
              <p className="text-xs text-muted-foreground mt-1">
                {`LSTM Error: ${(((payload[1].value - payload[0].value) / payload[0].value) * 100).toFixed(2)}%`}
              </p>
            )}
            {payload.length > 2 && payload[0]?.value && payload[2]?.value && (
              <p className="text-xs text-muted-foreground">
                {`RL Error: ${(((payload[2].value - payload[0].value) / payload[0].value) * 100).toFixed(2)}%`}
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };
  
  if (!chartData.length) {
    return (
      <div className="w-full bg-card border rounded-xl overflow-hidden p-6 h-[400px]">
        <h3 className="text-xl font-semibold mb-4">{title || `${stockData.symbol} Backtested Results`}</h3>
        <div className="flex items-center justify-center h-[300px]">
          <p className="text-muted-foreground">No backtest data available</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full bg-card border rounded-xl overflow-hidden p-6 h-[400px]">
      <h3 className="text-xl font-semibold mb-4">{title || `${stockData.symbol} Backtested Results`}</h3>
      
      <ResponsiveContainer width="100%" height="80%">
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            scale="time" 
            tick={{ fontSize: 12 }}
            tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { year: '2-digit', month: 'short' })}
          />
          <YAxis 
            tickFormatter={formatYAxis}
            domain={['auto', 'auto']} 
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="actual" 
            stroke="#3b82f6" 
            name="Actual Data" 
            dot={false}
            strokeWidth={2} 
          />
          <Line 
            type="monotone" 
            dataKey="lstm" 
            stroke="#10b981" 
            name="LSTM Predicted" 
            strokeDasharray="3 3"
            dot={false} 
          />
          <Line 
            type="monotone" 
            dataKey="rl" 
            stroke="#ef4444" 
            name="RL Predicted" 
            strokeDasharray="5 5"
            dot={false} 
          />
          <Brush 
            dataKey="date" 
            height={30} 
            stroke="#8884d8"
            tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { year: 'numeric' })}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BacktestChart;
